/**
 * TitlePeriodsField — the title terms every Wisconsin offer prints (legal
 * copy: paperwork-copy-and-rules.v1.md §10, `WI.offer.title_periods.*`; QA N13
 * / D8).
 *
 * Shown on every Wisconsin offer (residential, condo or vacant land), whatever
 * contingencies are selected. Every field is required and nothing is
 * prefilled:
 *   - `title_commitment_days`: days after acceptance for the title commitment;
 *   - `title_objection_days`: days after Buyer receives it to object;
 *   - vacant land only: `title_commitment_party` (who gets and pays for the
 *     commitment, "seller" or "buyer") and `title_cure_days` (the seller's
 *     days to fix an objection).
 * Days are whole numbers from 1 to 365. Soft warnings (no block) say when the
 * commitment, or the objection period, could run past the target closing date
 * even if the offer were accepted today.
 *
 * The inputs are `aria-required`, not `required`: the workspace counter is a
 * real <form>, and native validation would block a price-only counter on an
 * older offer that has no periods, whose untouched terms carry forward. The
 * forms check these fields themselves (`titlePeriodsError`).
 *
 * Rendered by the offer wizard's Terms step and both counter forms. The values
 * live in the form's `contingencies` object; see `serializeContingencies`.
 *
 * Props:
 *   - category: the Wisconsin offer category (`offerTermsCategory`), or null
 *   - contingencies: form state
 *   - onChange(nextContingencies)
 *   - closingDate: the target closing date (YYYY-MM-DD), for the warnings
 *   - timeZone: the property's zone, for "today"
 *   - viewerRole: 'buyer' (default) or 'seller', for the help text
 *   - disabled
 *   - serverErrors: messages from a 422, keyed by field
 */
import React, { useId } from 'react';
import {
  MAX_DEADLINE_DAYS,
  MIN_DEADLINE_DAYS,
  TITLE_COMMITMENT_DAYS_KEY,
  TITLE_COMMITMENT_PARTIES,
  TITLE_COMMITMENT_PARTY_KEY,
  TITLE_CURE_DAYS_KEY,
  TITLE_OBJECTION_DAYS_KEY,
  TITLE_PERIODS_COPY as COPY,
  deadlineKey,
  getTitleCommitmentParty,
  landTitleAsked,
  parseDeadlineDays,
  titlePeriodsFieldErrors,
} from './contingencyCatalog';
import { dateInDaysInZone } from './offerExpiration';
import { formatDateOnly } from './utils';
import styles from './FinancingTermsField.module.css';

const textOf = (value) => (value === undefined || value === null ? '' : String(value));

/**
 * The closing-date warning for the title periods, or null: the commitment
 * would be due on or after the target closing date even with acceptance today
 * (`warning.delivery_after_closing`); otherwise the objection period could
 * still be running then (`warning.objection_after_closing`).
 *
 * @param {Object} contingencies - Form state.
 * @param {string|null|undefined} closingDate - YYYY-MM-DD.
 * @param {string|undefined} timeZone - The property's zone.
 * @returns {string|null}
 */
export const titlePeriodWarning = (contingencies, closingDate, timeZone) => {
  if (!closingDate) return null;
  const delivery = parseDeadlineDays(contingencies?.[TITLE_COMMITMENT_DAYS_KEY]);
  if (typeof delivery !== 'number') return null;
  const closingLabel = formatDateOnly(closingDate);
  if (dateInDaysInZone(delivery, timeZone) >= closingDate) {
    return COPY.warningDeliveryAfterClosing(closingLabel);
  }
  const objection = parseDeadlineDays(contingencies?.[TITLE_OBJECTION_DAYS_KEY]);
  if (typeof objection === 'number' && dateInDaysInZone(delivery + objection, timeZone) >= closingDate) {
    return COPY.warningObjectionAfterClosing(closingLabel);
  }
  return null;
};

function DaysInput({ id, fieldKey, label, help, value, onChange, error, serverError, disabled }) {
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
        <span className={styles.required} aria-hidden="true"> *</span>
      </label>
      <input
        id={id}
        className={styles.input}
        type="number"
        inputMode="numeric"
        min={MIN_DEADLINE_DAYS}
        max={MAX_DEADLINE_DAYS}
        step="1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-required="true"
        aria-describedby={[help && `${id}-help`, error && `${id}-error`].filter(Boolean).join(' ') || undefined}
        aria-invalid={Boolean(error || serverError) || undefined}
        data-testid={fieldKey}
      />
      {help && <p id={`${id}-help`} className={styles.hint}>{help}</p>}
      {error && <p id={`${id}-error`} className={styles.error} role="alert">{error}</p>}
      {serverError && serverError !== error && (
        <p className={styles.error} role="alert" data-testid={`contingency-error-${fieldKey}`}>{serverError}</p>
      )}
    </div>
  );
}

export default function TitlePeriodsField({
  category,
  contingencies,
  onChange,
  closingDate = null,
  timeZone,
  viewerRole = 'buyer',
  disabled = false,
  serverErrors = null,
}) {
  const id = useId();
  const helpId = `${id}-help`;
  const partyLabelId = `${id}-party-label`;

  if (!category) return null;

  const seller = viewerRole === 'seller';
  const land = landTitleAsked(category);
  const errors = titlePeriodsFieldErrors(contingencies, category);
  const typed = (key) => textOf(contingencies?.[key]).trim() !== '';
  const errorFor = (key) => (typed(key) ? errors[key] || null : null);
  const server = (key) => serverErrors?.[key] || null;
  const set = (key, value) => onChange({ ...(contingencies || {}), [key]: value });
  const party = getTitleCommitmentParty(contingencies);
  const periodsMissing = [TITLE_COMMITMENT_DAYS_KEY, TITLE_OBJECTION_DAYS_KEY].some((key) => !typed(key));
  const landMissing = land && (!party || !typed(TITLE_CURE_DAYS_KEY));
  const warning = titlePeriodWarning(contingencies, closingDate, timeZone);
  // Residential and condo: a title review deadline at or before the commitment.
  const reviewTooEarly = errors[deadlineKey('title')] || null;

  let help = seller ? COPY.helpSeller : COPY.help;
  if (land) help = seller ? COPY.land.helpSeller : COPY.land.help;
  let objectionHelp = seller ? COPY.objectionHelpSeller : COPY.objectionHelp;
  if (land && !seller) objectionHelp = COPY.land.objectionHelp;

  return (
    <fieldset className={styles.terms} data-testid="title-periods" aria-describedby={helpId}>
      <legend className={styles.title}>
        {COPY.title}
        <span className={styles.required} aria-hidden="true"> *</span>
      </legend>
      <p id={helpId} className={styles.help}>{help}</p>

      {land && (
        <div className={styles.field}>
          <span id={partyLabelId} className={styles.label}>
            {COPY.land.partyLabel}
            <span className={styles.required} aria-hidden="true"> *</span>
          </span>
          <div role="radiogroup" aria-labelledby={partyLabelId} aria-required="true" className={styles.types}>
            {TITLE_COMMITMENT_PARTIES.map((value) => (
              <label key={value} className={`${styles.type} ${party === value ? styles.typeOn : ''}`}>
                <input
                  type="radio"
                  name={`${id}-party`}
                  value={value}
                  checked={party === value}
                  onChange={() => set(TITLE_COMMITMENT_PARTY_KEY, value)}
                  disabled={disabled}
                  data-testid={`title-commitment-party-${value}`}
                />
                <span>{COPY.land.partyOption[value]}</span>
              </label>
            ))}
          </div>
          {server(TITLE_COMMITMENT_PARTY_KEY) && (
            <p className={styles.error} role="alert" data-testid={`contingency-error-${TITLE_COMMITMENT_PARTY_KEY}`}>
              {server(TITLE_COMMITMENT_PARTY_KEY)}
            </p>
          )}
        </div>
      )}

      <div className={styles.grid}>
        <DaysInput
          id={`${id}-delivery`}
          fieldKey={TITLE_COMMITMENT_DAYS_KEY}
          label={land ? COPY.land.deliveryLabel : COPY.deliveryLabel}
          help={COPY.deliveryHelp}
          value={textOf(contingencies?.[TITLE_COMMITMENT_DAYS_KEY])}
          onChange={(text) => set(TITLE_COMMITMENT_DAYS_KEY, text)}
          error={errorFor(TITLE_COMMITMENT_DAYS_KEY) || reviewTooEarly}
          serverError={server(TITLE_COMMITMENT_DAYS_KEY)}
          disabled={disabled}
        />
        <DaysInput
          id={`${id}-objection`}
          fieldKey={TITLE_OBJECTION_DAYS_KEY}
          label={seller ? COPY.objectionLabelSeller : COPY.objectionLabel}
          help={objectionHelp}
          value={textOf(contingencies?.[TITLE_OBJECTION_DAYS_KEY])}
          onChange={(text) => set(TITLE_OBJECTION_DAYS_KEY, text)}
          error={errorFor(TITLE_OBJECTION_DAYS_KEY)}
          serverError={server(TITLE_OBJECTION_DAYS_KEY)}
          disabled={disabled}
        />
        {land && (
          <DaysInput
            id={`${id}-cure`}
            fieldKey={TITLE_CURE_DAYS_KEY}
            label={COPY.land.cureDaysLabel}
            help={seller ? null : COPY.land.cureDaysHelp}
            value={textOf(contingencies?.[TITLE_CURE_DAYS_KEY])}
            onChange={(text) => set(TITLE_CURE_DAYS_KEY, text)}
            error={errorFor(TITLE_CURE_DAYS_KEY)}
            serverError={server(TITLE_CURE_DAYS_KEY)}
            disabled={disabled}
          />
        )}
      </div>

      {warning && <p className={styles.warning} role="status" data-testid="title-periods-warning">{warning}</p>}
      {periodsMissing && <p className={styles.missing} data-testid="title-periods-missing">{COPY.missing}</p>}
      {landMissing && <p className={styles.missing} data-testid="land-title-missing">{COPY.land.missing}</p>}
    </fieldset>
  );
}
